import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { ChevronDown, RotateCcw, Save, Send, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const RECIPIENT_STYLE = {
  client: "bg-blue-50 text-blue-700 border-blue-200",
  driver: "bg-amber-50 text-amber-700 border-amber-200",
  partner: "bg-violet-50 text-violet-700 border-violet-200",
  admin: "bg-slate-100 text-slate-700 border-slate-200",
};

function TemplateRow({ tpl, t }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(tpl.subject);
  const [html, setHtml] = useState(tpl.html);
  const [preview, setPreview] = useState("");
  const [testTo, setTestTo] = useState("");
  const [custom, setCustom] = useState(tpl.is_custom);
  const timer = useRef(null);

  const runPreview = useCallback(async (subj, body) => {
    try {
      const { data } = await api.post("/admin/email-templates/preview", { key: tpl.key, subject: subj, html: body });
      setPreview(data.html);
    } catch (e) { /* ignore preview errors */ }
  }, [tpl.key]);

  useEffect(() => {
    if (open && !preview) runPreview(subject, html);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onEdit = (subj, body) => {
    setSubject(subj); setHtml(body);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => runPreview(subj, body), 500);
  };

  const save = async () => {
    try {
      await api.put(`/admin/email-templates/${tpl.key}`, { subject, html });
      setCustom(true); toast.success(t("mail.saved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reset = async () => {
    try {
      await api.post(`/admin/email-templates/${tpl.key}/reset`);
      setSubject(tpl.default_subject); setHtml(tpl.default_html); setCustom(false);
      runPreview(tpl.default_subject, tpl.default_html);
      toast.success(t("mail.reseted"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error(t("toast.emailDestRequired")); return; }
    try {
      const { data } = await api.post(`/admin/email-templates/${tpl.key}/test`, { to: testTo.trim() });
      if (data.status === "sent") toast.success(t("toast.emailSent"));
      else if (data.status === "mocked") toast.info(t("toast.emailMocked"));
      else toast.error(`${data.error || data.status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const vars = Object.keys(tpl.sample || {});

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`mail-tpl-${tpl.key}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-50 transition-colors"
        data-testid={`mail-tpl-${tpl.key}-toggle`}>
        <Mail size={18} className="text-slate-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-800 text-sm truncate">{t(`mail.${tpl.key}.name`)}</div>
          <div className="text-xs text-slate-500 truncate">{t(`mail.${tpl.key}.desc`)}</div>
        </div>
        <span className={`hidden sm:inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${RECIPIENT_STYLE[tpl.recipient] || RECIPIENT_STYLE.client}`}>
          {t(`mail.r.${tpl.recipient}`)}
        </span>
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${custom ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
          {custom ? t("mail.custom") : t("mail.default")}
        </span>
        <ChevronDown size={18} className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="grid lg:grid-cols-2 gap-5 p-4 border-t border-slate-100 bg-slate-50/60">
          <div className="space-y-3">
            <div className="text-xs text-slate-500 flex items-start gap-1.5">
              <span className="font-semibold text-slate-600">{t("mail.trigger")}:</span>
              <span>{t(`mail.${tpl.key}.desc`)}</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("mail.subject")}</Label>
              <Input value={subject} onChange={(e) => onEdit(e.target.value, html)} data-testid={`mail-${tpl.key}-subject`} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("mail.body")}</Label>
              <Textarea rows={8} value={html} onChange={(e) => onEdit(subject, e.target.value)} className="font-mono text-xs" data-testid={`mail-${tpl.key}-html`} />
            </div>
            {vars.length > 0 && (
              <div className="text-xs text-slate-500">
                <span className="font-semibold text-slate-600">{t("mail.vars")}:</span>{" "}
                {vars.map((v) => (
                  <code key={v} className="inline-block bg-white border border-slate-200 rounded px-1.5 py-0.5 mr-1 mb-1 text-[11px] text-blue-800">{`{{${v}}}`}</code>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button size="sm" onClick={save} className="bg-blue-900 hover:bg-blue-800" data-testid={`mail-${tpl.key}-save`}>
                <Save size={14} className="mr-1.5" /> {t("common.save")}
              </Button>
              <Button size="sm" variant="outline" onClick={reset} data-testid={`mail-${tpl.key}-reset`}>
                <RotateCcw size={14} className="mr-1.5" /> {t("mail.reset")}
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Input placeholder={t("mail.sendTest")} value={testTo} onChange={(e) => setTestTo(e.target.value)} className="max-w-[220px] h-9" data-testid={`mail-${tpl.key}-test-input`} />
              <Button size="sm" variant="outline" onClick={sendTest} data-testid={`mail-${tpl.key}-test-btn`}>
                <Send size={14} className="mr-1.5" /> {t("email.sendTest")}
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">{t("mail.preview")}</Label>
            <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
              <iframe
                title={`preview-${tpl.key}`}
                srcDoc={preview}
                sandbox=""
                className="w-full h-[420px] border-0"
                data-testid={`mail-${tpl.key}-preview`} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function EmailTemplates() {
  const { t } = useI18n();
  const [list, setList] = useState([]);

  useEffect(() => {
    api.get("/admin/email-templates").then(({ data }) => setList(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  return (
    <div className="space-y-3" data-testid="email-templates-panel">
      <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3.5 py-2.5">
        <Mail size={16} className="text-blue-700 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-900">{t("mail.subtitle")}</p>
      </div>
      {list.map((tpl) => <TemplateRow key={tpl.key} tpl={tpl} t={t} />)}
    </div>
  );
}
